/**
 * CarCard Molecule Component
 * Displays car information in a card format
 */

'use client'

import {
	CarImage,
	PriceTag,
	RatingStars,
	StatusBadge,
} from '@/components/atoms'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { Car } from '@/types'
import { Briefcase, Gauge, Users } from 'lucide-react'

interface CarCardProps {
	car: Car
	onSelect?: (car: Car) => void
	className?: string
}

export function CarCard({ car, onSelect, className }: CarCardProps) {
	const primaryImage = car.images.find(img => img.isPrimary) || car.images[0]

	return (
		<Card
			className={cn(
				'overflow-hidden hover:shadow-lg transition-shadow',
				className
			)}
		>
			{/* Image */}
			<div className='relative w-full aspect-video bg-gray-100'>
				<CarImage
					src={primaryImage?.url || ''}
					alt={primaryImage?.alt || `${car.brand} ${car.model}`}
					fill
					objectFit='contain'
				/>

				{/* Availability Badge */}
				<div className='absolute top-3 right-3'>
					<StatusBadge
						status={car.availability ? 'available' : 'unavailable'}
					/>
				</div>
			</div>

			<CardContent className='p-4'>
				{/* Car Info */}
				<div className='mb-3'>
					<h3 className='text-lg font-semibold text-[#09194D]'>
						{car.brand} {car.model}
					</h3>
					<p className='text-sm text-gray-600'>{car.year}</p>
				</div>

				{/* Rating */}
				<div className='mb-3'>
					<RatingStars
						rating={car.rating}
						reviewCount={car.reviewCount}
						size='sm'
					/>
				</div>

				{/* Specifications */}
				<div className='flex items-center gap-4 text-sm text-gray-600 mb-4'>
					<div className='flex items-center gap-1'>
						<Users className='w-4 h-4' />
						<span>{car.specifications.seats}</span>
					</div>
					<div className='flex items-center gap-1'>
						<Briefcase className='w-4 h-4' />
						<span>{car.specifications.luggage}</span>
					</div>
					<div className='flex items-center gap-1'>
						<Gauge className='w-4 h-4' />
						<span className='capitalize'>
							{car.specifications.transmission}
						</span>
					</div>
				</div>

				{/* Price */}
				<PriceTag
					amount={car.pricing.dailyRate}
					currency={car.pricing.currency}
					period='day'
					size='lg'
				/>
			</CardContent>

			<CardFooter className='p-4 pt-0'>
				<Button
					onClick={() => onSelect?.(car)}
					disabled={!car.availability}
					className='w-full bg-[#09194D] hover:bg-[#09194D]/90 text-white cursor-pointer'
				>
					{car.availability ? 'Rentar Ahora' : 'No Disponible'}
				</Button>
			</CardFooter>
		</Card>
	)
}
